import React, { useState, useEffect } from 'react';
import sdApi from './sdApi';

const defaultConfig = {
  steps: 30,
  cfg_scale: 4.5,
  width: 1024,
  height: 768,
  seed: -1,
  sampler: 'DPM++ 2M',
  scheduler: 'Karras',
  negative_prompt: 'horrible, low quality, blurry, distorted, deformed, ugly, bad, low resolution',
  prompt: '',
  image_size_array: [
    { label: '1024×1024', w: 1024, h: 1024 },
    { label: '1024×768', w: 1024, h: 768 },
    { label: '768×1024', w: 768, h: 1024 },
    { label: '1280×720', w: 1280, h: 720 },
  ],
};

function Txt2Img({ onImageGenerated, loras = [], onRefreshLoras, isLoadingLoras }) {
  const [prompt, setPrompt] = useState(defaultConfig.prompt);
  const [negativePrompt, setNegativePrompt] = useState(defaultConfig.negative_prompt);
  const [steps, setSteps] = useState(defaultConfig.steps);
  const [cfgScale, setCfgScale] = useState(defaultConfig.cfg_scale);
  const [width, setWidth] = useState(defaultConfig.width);
  const [height, setHeight] = useState(defaultConfig.height);
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [seed, setSeed] = useState(defaultConfig.seed);
  const [sampler, setSampler] = useState(defaultConfig.sampler);
  const [samplers, setSamplers] = useState([]);
  const [scheduler, setScheduler] = useState(defaultConfig.scheduler);
  const [schedulers, setSchedulers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState(null);
  const [selectedLora, setSelectedLora] = useState('');
  const [loraWeight, setLoraWeight] = useState(0.8);

  useEffect(() => {
    sdApi.getSamplers().then(data => setSamplers(data)).catch(() => {});
    sdApi.getSchedulers().then(data => setSchedulers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(async () => {
        try {
          const progressData = await sdApi.getProgress();
          setProgress(Math.round(progressData.progress * 100));
        } catch (err) {}
      }, 500);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('请输入提示词');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      const result = await sdApi.txt2img({
        prompt,
        negative_prompt: negativePrompt,
        steps,
        cfg_scale: cfgScale,
        width,
        height,
        seed,
        sampler_name: sampler,
        scheduler: scheduler,
      });

      if (result.images && result.images.length > 0) {
        const imageData = `data:image/png;base64,${result.images[0]}`;
        setGeneratedImage(imageData);
        if (onImageGenerated) {
          onImageGenerated({
            image: imageData,
            info: JSON.parse(result.info),
            prompt,
            timestamp: new Date().toISOString(),
          });
        }
      }
    } catch (err) {
      setError(`生成失败: ${err.message}`);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const handleInterrupt = async () => {
    try { await sdApi.interrupt(); } catch (err) {}
  };

  const handleCustomSize = () => {
    const w = parseInt(customWidth);
    const h = parseInt(customHeight);
    if (w > 0 && h > 0) {
      setWidth(w);
      setHeight(h);
    }
  };

  const handleAddLora = () => {
    if (!selectedLora) return;
    const loraTag = `<lora:${selectedLora}:${loraWeight}>`;
    if (!prompt.includes(`<lora:${selectedLora}:`)) {
      setPrompt(prev => prev ? `${prev} ${loraTag}` : loraTag);
    }
  };

  const handleRemoveLora = (loraName) => {
    const loraRegex = new RegExp(`\\s*<lora:${loraName}:[^>]+>`, 'g');
    setPrompt(prev => prev.replace(loraRegex, '').trim());
  };

  const getUsedLoras = () => {
    const loraRegex = /<lora:([^:]+):([^>]+)>/g;
    const matches = [];
    let match;
    while ((match = loraRegex.exec(prompt)) !== null) {
      matches.push({ name: match[1], weight: parseFloat(match[2]) });
    }
    return matches;
  };

  const usedLoras = getUsedLoras();

  return (
    <div className="sd-generator-panel">
      <div className="sd-panel-header">
        <h2>✨ 文生图 Text to Image</h2>
      </div>

      <div className="sd-generator-content">
        <div className="sd-input-section">
          <div className="sd-input-group">
            <label>提示词 Prompt</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="描述你想要生成的图像..."
              rows={4}
            />
          </div>

          <div className="sd-input-group">
            <label>负面提示词 Negative Prompt</label>
            <textarea
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
              placeholder="描述你不想要的内容..."
              rows={3}
            />
          </div>

          <div className="sd-input-group sd-lora-section">
            <div className="sd-lora-header">
              <label>🎭 Lora</label>
              <button className="sd-refresh-btn" onClick={onRefreshLoras} disabled={isLoadingLoras}>
                {isLoadingLoras ? '⏳' : '🔄'}
              </button>
            </div>
            <div className="sd-lora-selector">
              <select value={selectedLora} onChange={(e) => setSelectedLora(e.target.value)}>
                <option value="">选择 Lora...</option>
                {loras.map(lora => <option key={lora.name} value={lora.name}>{lora.name}</option>)}
              </select>
              <div className="sd-lora-weight">
                <label>权重: {loraWeight}</label>
                <input type="range" min="0" max="2" step="0.1" value={loraWeight} onChange={(e) => setLoraWeight(parseFloat(e.target.value))} />
              </div>
              <button className="sd-add-lora-btn" onClick={handleAddLora} disabled={!selectedLora}>➕ 添加</button>
            </div>
            {usedLoras.length > 0 && (
              <div className="sd-used-loras">
                <label>已使用的 Lora:</label>
                <div className="sd-lora-tags">
                  {usedLoras.map(lora => (
                    <span key={lora.name} className="sd-lora-tag">
                      {lora.name} ({lora.weight})
                      <button onClick={() => handleRemoveLora(lora.name)}>✕</button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="sd-params-grid">
            <div className="sd-input-group">
              <label>采样器</label>
              <select value={sampler} onChange={(e) => setSampler(e.target.value)}>
                {samplers.length > 0 ? samplers.map(s => <option key={s.name} value={s.name}>{s.name}</option>) : (
                  <>
                    <option value="DPM++ 2M">DPM++ 2M</option>
                    <option value="Euler a">Euler a</option>
                    <option value="Euler">Euler</option>
                  </>
                )}
              </select>
            </div>

            <div className="sd-input-group">
              <label>调度类型</label>
              <select value={scheduler} onChange={(e) => setScheduler(e.target.value)}>
                {schedulers.length > 0 ? schedulers.map(s => <option key={s.name} value={s.name}>{s.name}</option>) : (
                  <>
                    <option value="Karras">Karras</option>
                    <option value="Normal">Normal</option>
                  </>
                )}
              </select>
            </div>

            <div className="sd-input-group">
              <label>步数 Steps: {steps}</label>
              <input type="range" min="1" max="150" value={steps} onChange={(e) => setSteps(Number(e.target.value))} />
            </div>

            <div className="sd-input-group">
              <label>CFG Scale: {cfgScale}</label>
              <input type="range" min="1" max="30" step="0.5" value={cfgScale} onChange={(e) => setCfgScale(Number(e.target.value))} />
            </div>

            <div className="sd-input-group">
              <label>种子 Seed</label>
              <input type="number" value={seed} onChange={(e) => setSeed(Number(e.target.value))} placeholder="-1 为随机" />
            </div>
          </div>

          <div className="sd-input-group">
            <label>图像尺寸 (当前: {width}×{height})</label>
            <div className="sd-size-buttons">
              {defaultConfig.image_size_array.map(size => (
                <button
                  key={size.label}
                  className={`sd-size-btn ${width === size.w && height === size.h ? 'active' : ''}`}
                  onClick={() => { setWidth(size.w); setHeight(size.h); }}
                >
                  {size.label}
                </button>
              ))}
            </div>
            <div className="sd-custom-size">
              <input type="number" placeholder="宽度" value={customWidth} onChange={(e) => setCustomWidth(e.target.value)} min="64" max="2048" />
              <span>×</span>
              <input type="number" placeholder="高度" value={customHeight} onChange={(e) => setCustomHeight(e.target.value)} min="64" max="2048" />
              <button className="sd-apply-btn" onClick={handleCustomSize}>应用</button>
            </div>
          </div>

          {error && <div className="sd-error">{error}</div>}

          <div className="sd-action-buttons">
            <button className="sd-generate-btn" onClick={handleGenerate} disabled={loading}>
              {loading ? `生成中... ${progress}%` : '🎨 生成图像'}
            </button>
            {loading && <button className="sd-interrupt-btn" onClick={handleInterrupt}>⏹ 中断</button>}
          </div>

          {loading && (
            <div className="sd-progress-bar">
              <div className="sd-progress-fill" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>

        <div className="sd-preview-section">
          <div className="sd-preview-container">
            {generatedImage ? (
              <img src={generatedImage} alt="Generated" />
            ) : (
              <div className="sd-preview-placeholder">
                <span>🖼️</span>
                <p>生成的图像将显示在这里</p>
              </div>
            )}
          </div>
          {generatedImage && (
            <a href={generatedImage} download={`sd-${Date.now()}.png`} className="sd-download-btn">
              📥 下载图像
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default Txt2Img;

